import { authService } from '../clients/ApiClients';
import { jwtDecode } from 'jwt-decode';
import { UserType } from '../constants/UserTypes';

/**
 * The management JWT's payload.
 *
 * Deliberately has no `email` or `full_name`: the backend's `Claims`
 * struct never carried them. Reading them off the token silently
 * produced `undefined` everywhere, which is why the sidebar avatar was
 * blank and outgoing messages were signed "HOA Administrator". The
 * signed-in user's name and email come from `GET /user/{id}` via
 * `UserContext`.
 */
interface DecodedToken {
  exp: number;
  sub?: string;
  user_type?: string;
  [key: string]: any;
}

export const getToken = (): string | null => {
  let token = localStorage.getItem('token');
  if (
    token === null ||
    token === undefined ||
    token === '' ||
    token === 'undefined'
  ) {
    token = sessionStorage.getItem('token');
  }
  if (
    token === null ||
    token === undefined ||
    token === '' ||
    token === 'undefined'
  ) {
    return null;
  }
  return token;
};

export const getRefreshToken = (): string | null => {
  let refreshToken =
    localStorage.getItem('refreshToken') ||
    sessionStorage.getItem('refreshToken');
  return refreshToken && refreshToken !== 'undefined' ? refreshToken : null;
};

export const getUserName = (): string | null => {
  let userName = localStorage.getItem('username');
  if (
    userName === null ||
    userName === undefined ||
    userName === '' ||
    userName === 'undefined'
  ) {
    userName = sessionStorage.getItem('username');
  }
  if (
    userName === null ||
    userName === undefined ||
    userName === '' ||
    userName === 'undefined'
  ) {
    return null;
  }
  return userName;
};

export const getRememberMe = (): string | null => {
  let rememberme = localStorage.getItem('rememberme');
  if (
    rememberme === null ||
    rememberme === undefined ||
    rememberme === '' ||
    rememberme === 'undefined' ||
    rememberme === 'false'
  ) {
    rememberme = sessionStorage.getItem('rememberme');
  }
  if (
    rememberme === null ||
    rememberme === undefined ||
    rememberme === '' ||
    rememberme === 'undefined' ||
    rememberme === 'false'
  ) {
    return null;
  }
  return rememberme;
};

// ============================================================================
// Token change notification
// ============================================================================

/**
 * Subscribers notified whenever the stored token changes.
 *
 * Storage is not reactive, so anything deriving state from the token has
 * to be told when it moves. Reading `getToken()` during render only works
 * if the component happens to re-render at the right moment, and
 * `UserProvider` does not: it sits above `<Routes>`, and `<App />` is a
 * stable element created once in `index.tsx`, so React skips re-rendering
 * it when the router navigates. The provider therefore kept the previous
 * user's profile across a sign-out and sign-in — a manager could see the
 * owner's email.
 */
type TokenListener = () => void;

const tokenListeners = new Set<TokenListener>();

const notifyTokenChange = (): void => {
  tokenListeners.forEach((listener) => listener());
};

/**
 * Subscribe to token changes, for `useSyncExternalStore`.
 *
 * Also listens for the `storage` event so signing out in one tab takes
 * effect in the others.
 */
export const subscribeToToken = (listener: TokenListener): (() => void) => {
  tokenListeners.add(listener);

  const onStorage = (event: StorageEvent): void => {
    // `key === null` is a `localStorage.clear()` from another tab.
    if (event.key === null || event.key === 'token') {
      listener();
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    tokenListeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
};

export const clearToken = (): void => {
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
  notifyTokenChange();
};

export const clearRefreshToken = (): void => {
  localStorage.removeItem('refreshToken');
  sessionStorage.removeItem('refreshToken');
};

export const clearUserName = (): void => {
  localStorage.removeItem('username');
  sessionStorage.removeItem('username');
};

export const clearRememberMe = (): void => {
  localStorage.removeItem('rememberme');
  sessionStorage.removeItem('rememberme');
};

export const clearSessionStorage = (): void => {
  sessionStorage.clear();
  notifyTokenChange();
};

export const clearAllTokens = (): void => {
  clearToken();
  clearRefreshToken();
};

export const clearAll = (): void => {
  clearAllTokens();
  clearUserName();
  clearRememberMe();
  clearSessionStorage();
};

/**
 * Store the refresh token beside the access token.
 *
 * Does not notify: nothing derives rendered state from the refresh token.
 * It lives here so `TokenUtils` owns every write to the token keys, which
 * is the invariant `TokenUtils.test.ts` checks — a direct
 * `storage.setItem('token', …)` elsewhere silently bypasses the change
 * notification that `UserProvider` depends on.
 */
export const setRefreshToken = (
  refreshToken: string,
  isRememberMe: boolean
): void => {
  if (isRememberMe) {
    localStorage.setItem('refreshToken', refreshToken);
  } else {
    sessionStorage.setItem('refreshToken', refreshToken);
  }
};

export const setToken = (token: string, isRememberMe: boolean): void => {
  if (isRememberMe) {
    localStorage.setItem('token', token);
  } else {
    sessionStorage.setItem('token', token);
  }
  notifyTokenChange();
};

/**
 * Exchange a refresh token for a new access token.
 *
 * **The backend rotates the refresh token too.** `AuthService::refresh_session`
 * calls `create_token_pair` and returns a fresh `refresh_token` beside the
 * access token. Storing only the access token — which this module used to
 * do — leaves the original refresh token in place until it expires, at
 * which point an actively-used session dies for no visible reason: a
 * manager signing in on Monday was signed out the following Monday
 * however continuously they had been working, because every refresh
 * discarded the replacement and kept renewing against the original.
 *
 * The old token stays valid until then — `token_version` is unchanged by a
 * refresh — so this was never a matter of anything outliving its welcome,
 * only of the seven-day window failing to roll forward.
 *
 * The `as { access_token: string }` cast that used to sit here is gone with
 * it. `TokenResponse` declares both fields, so the cast narrowed the
 * response to the one field this function then read — which is a fair
 * description of how the other one came to be dropped.
 *
 * The member portal hit this first and was fixed on its own; this is the
 * same fix, arriving late to the app that shares the shape.
 */
/**
 * The refresh currently in flight, if any.
 *
 * **Without this, rotation signs everybody out roughly hourly.** The
 * request interceptor in `ApiClients` calls `refreshAccessToken` once per
 * request, so a screen that fires four calls against an expired access
 * token sends four refreshes carrying the *same* refresh token. That was
 * harmless while the old token stayed valid; now that the backend rotates
 * on use, three of those four are replays, and a replay outside the
 * server's grace window revokes the whole session family.
 *
 * So concurrent callers share one request and one answer. The promise is
 * cleared when it settles, so the *next* refresh after this one finishes
 * starts fresh rather than replaying a cached result.
 *
 * This does not span tabs — two tabs each have their own module
 * instance and race through shared storage. That case is covered by the
 * grace window on the server (`REUSE_GRACE_SECS`), which is deliberately
 * the authority: a guard that lives only here would be undone by any
 * future caller that forgets to use it.
 */
let inFlightRefresh: Promise<string | null> | null = null;

export const refreshAccessToken = async (
  refreshToken: string
): Promise<string | null> => {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  inFlightRefresh = (async (): Promise<string | null> => {
    try {
      const response = await authService.refreshToken({
        refreshRequest: { refresh_token: refreshToken },
      });
      if (response.data.refresh_token) {
        setRefreshToken(response.data.refresh_token, !!getRememberMe());
      }
      return response.data.access_token ?? null;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return null;
    }
  })();

  try {
    return await inFlightRefresh;
  } finally {
    inFlightRefresh = null;
  }
};

/**
 * What the server says about a session (#635).
 *
 * - `valid`: the token is good, or an expired one was refreshed.
 * - `invalid`: the server refused it, or there is nothing to refresh with.
 * - `unreachable`: no answer. A background check must not sign somebody
 *   out for a dropped connection; the next request will tell.
 */
export type SessionStatus = 'valid' | 'invalid' | 'unreachable';

export const sessionStatus = async (
  token: string | null
): Promise<SessionStatus> => {
  if (token === null) {
    return 'invalid';
  }
  let decodedToken: DecodedToken;
  try {
    decodedToken = jwtDecode<DecodedToken>(token);
  } catch {
    return 'invalid';
  }

  // Expired: a refresh decides it.
  if (decodedToken.exp < Math.floor(Date.now() / 1000)) {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return 'invalid';
    }
    const newToken = await refreshAccessToken(refreshToken);
    if (!newToken) {
      return 'invalid';
    }
    setToken(newToken, !!getRememberMe());
    return 'valid';
  }

  try {
    const response = await authService.verifyToken({
      verifyRequest: { token },
    });
    return (response.data as { valid: boolean }).valid ? 'valid' : 'invalid';
  } catch (error) {
    // An answer that is not a 2xx is a refusal; no answer is not.
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (status !== undefined && status < 500) {
      return 'invalid';
    }
    console.error('Token verification failed:', error);
    return 'unreachable';
  }
};

/**
 * Whether the session is good right now, as a yes or no. Anything but a
 * clear yes is a no — the sign-in page's "already signed in?" question.
 */
export const verifyToken = async (token: string | null): Promise<boolean> =>
  (await sessionStatus(token)) === 'valid';

/**
 * Whether the stored token has not expired yet, without asking anyone.
 * Enough to render the app while the server is asked in the background.
 */
export const tokenLooksLive = (token: string | null = getToken()): boolean => {
  if (token === null) {
    return false;
  }
  try {
    return jwtDecode<DecodedToken>(token).exp > Math.floor(Date.now() / 1000);
  } catch {
    return false;
  }
};

export const tokenIsUnexpired = async (
  token: string | null
): Promise<boolean> => {
  try {
    if (token === null) {
      return false;
    }

    // Decode the token
    const decodedToken = jwtDecode<DecodedToken>(token);
    const currentTime = Math.floor(Date.now() / 1000);

    // Check if the token is expired
    if (decodedToken.exp < currentTime) {
      // Token is expired, try to refresh
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        const newToken = await refreshAccessToken(refreshToken);
        if (newToken) {
          // Update the token in storage
          setToken(newToken, !!getRememberMe());
          return true;
        }
      }
      return false;
    }

    return true;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

export const generateInitials = (name: string): string => {
  const names = name.split(' ').filter((n) => n.length > 0);
  if (names.length === 0) return '';
  if (names.length === 1) {
    return names[0].length > 1
      ? `${names[0][0]}${names[0][names[0].length - 1]}`.toUpperCase()
      : names[0][0].toUpperCase();
  }
  return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
};

export const getUserIdFromToken = (): string | null => {
  const token = getToken();
  if (!token) return null;

  try {
    const decodedToken = jwtDecode<DecodedToken>(token);
    return decodedToken.sub || null;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const getUserTypeFromToken = (): string | null => {
  const token = getToken();
  if (!token) return null;

  try {
    const decodedToken = jwtDecode<DecodedToken>(token);
    return decodedToken.user_type || null;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const getCompanyIdFromToken = (): string | null => {
  const token = getToken();
  if (!token) return null;

  try {
    const decodedToken = jwtDecode<DecodedToken>(token);
    return decodedToken.company_id || null;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const getLoginRoute = (): string | null => {
  const userType = getUserTypeFromToken();
  switch (userType) {
    case UserType.SUPER_ADMIN:
    case UserType.OWNER:
    case UserType.ADMIN:
    case UserType.MANAGER:
    case UserType.BOARD_MEMBER:
      return '/home';
    default:
      console.log('no user type match :(' + userType);
      return null;
  }
};

export const hasManagerPermission = (): boolean => {
  const userType = getUserTypeFromToken();
  // Manager-level access or above (SuperAdmin, Owner, Admin, Manager)
  const managerRoles: Array<string> = [
    UserType.SUPER_ADMIN,
    UserType.OWNER,
    UserType.ADMIN,
    UserType.MANAGER,
  ];

  return userType !== null && managerRoles.includes(userType);
};

export const canDeleteVendor = (): boolean => {
  const userType = getUserTypeFromToken();
  // Only SuperAdmin, Owner, and Admin can delete vendors
  // Managers and BoardMembers cannot delete
  const deleteRoles: Array<string> = [
    UserType.SUPER_ADMIN,
    UserType.OWNER,
    UserType.ADMIN,
  ];

  return userType !== null && deleteRoles.includes(userType);
};
